import { useState } from "react";
import { useContextoApp } from "@/app/contexto";
import { api, ApiError } from "@/lib/api";
import { errorPeriodo, guardarArchivo, periodoInicial, REPORTES, rutaReporte, type Periodo, type Reporte, type ReporteId } from "@/lib/reportes";

interface EstadoReporte {
  cargando: boolean;
  error: string | null;
}

const SIN_ESTADO: EstadoReporte = { cargando: false, error: null };

function estadosIniciales(): Record<ReporteId, EstadoReporte> {
  return Object.fromEntries(REPORTES.map((r) => [r.id, SIN_ESTADO])) as Record<ReporteId, EstadoReporte>;
}

/**
 * Lógica de Reportes (sin JSX): un período compartido por las cuatro descargas (presupuesto lo ignora) y, por
 * reporte, su propio loading/error — cada tarjeta baja el suyo de forma independiente. La validación del
 * período (`errorPeriodo`) corre antes de pedir nada; el error del backend, si lo hay, llega tal cual en el
 * `detail` de la respuesta (`ApiError.message`).
 */
export function useReportesTab() {
  const { fid } = useContextoApp();
  const [periodo, setPeriodo] = useState<Periodo>(() => periodoInicial());
  const [estados, setEstados] = useState<Record<ReporteId, EstadoReporte>>(estadosIniciales);

  const cambiarPeriodo = (campo: keyof Periodo, valor: string) => setPeriodo((p) => ({ ...p, [campo]: valor }));

  const estadoDe = (id: ReporteId): EstadoReporte => estados[id] ?? SIN_ESTADO;

  const descargar = async (r: Reporte) => {
    if (estadoDe(r.id).cargando) return;
    const errorPrevio = errorPeriodo(r, periodo);
    if (errorPrevio) {
      setEstados((e) => ({ ...e, [r.id]: { cargando: false, error: errorPrevio } }));
      return;
    }
    setEstados((e) => ({ ...e, [r.id]: { cargando: true, error: null } }));
    try {
      const descarga = await api.descargar(rutaReporte(fid, r, periodo));
      guardarArchivo(descarga);
      setEstados((e) => ({ ...e, [r.id]: { cargando: false, error: null } }));
    } catch (err) {
      const mensaje = err instanceof ApiError ? err.message : "Ocurrió un error inesperado";
      setEstados((e) => ({ ...e, [r.id]: { cargando: false, error: mensaje } }));
    }
  };

  return {
    reportes: REPORTES,
    periodo,
    cambiarPeriodo,
    descargar,
    cargando: (id: ReporteId) => estadoDe(id).cargando,
    error: (id: ReporteId) => estadoDe(id).error,
  };
}
